import { API_BASE } from './config'
import { getRequiredAuthToken, parseApiError, type UserPublic } from './auth'

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_PROFILE_BYTES = 5 * 1024 * 1024
const MAX_COVER_BYTES = 10 * 1024 * 1024

function validateImageFile(file: File, maxBytes: number): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Use a JPEG, PNG, or WebP image.'
  }
  if (file.size > maxBytes) {
    const mb = Math.floor(maxBytes / (1024 * 1024))
    return `Image must be ${mb} MB or smaller.`
  }
  return null
}

async function uploadImage(
  path: string,
  file: File,
  maxBytes: number,
  loginMessage: string,
): Promise<UserPublic> {
  const validationError = validateImageFile(file, maxBytes)
  if (validationError) throw new Error(validationError)

  const token = getRequiredAuthToken(loginMessage)

  const form = new FormData()
  form.append('file', file)

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function uploadProfileImage(file: File): Promise<UserPublic> {
  return uploadImage(
    '/users/me/profile-image',
    file,
    MAX_PROFILE_BYTES,
    'Log in to update your profile photo.',
  )
}

export async function updateProfile(payload: {
  bio?: string
  emailNotificationsEnabled?: boolean
}): Promise<UserPublic> {
  const token = getRequiredAuthToken('Log in to update your profile.')

  const res = await fetch(`${API_BASE}/users/me`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function fetchUserByUsername(username: string): Promise<UserPublic> {
  const normalized = username.trim().replace(/^@/, '')
  const res = await fetch(
    `${API_BASE}/users/by-username/${encodeURIComponent(normalized)}`,
  )
  if (!res.ok) throw new Error(await parseApiError(res))
  return res.json()
}

export async function uploadCoverImage(file: File): Promise<UserPublic> {
  return uploadImage(
    '/users/me/cover-image',
    file,
    MAX_COVER_BYTES,
    'Log in to update your cover image.',
  )
}
